import React from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";

export default function LetterOfCreditPage() {
  const [rows, setRows] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ reference: "", beneficiary: "", applicant: "", amount: "", currency: "USD", lc_type: "sight", issue_date: new Date().toISOString().slice(0, 10), expiry_date: "", goods_description: "", collateral_account_id: "", collateral_amount: "" });

  const load = async () => {
    const [{ data }, { data: accts }] = await Promise.all([api.get("/letters-of-credit"), api.get("/bank-accounts")]);
    setRows(data); setAccounts(accts);
  };
  React.useEffect(() => { load(); }, []);

  const acctName = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.name} · ${a.currency}` : "—"; };

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/letters-of-credit", {
      ...f,
      amount: parseFloat(f.amount),
      collateral_account_id: f.collateral_account_id || null,
      collateral_amount: f.collateral_amount ? parseFloat(f.collateral_amount) : 0,
    });
    setOpen(false); setF({ ...f, reference: "", beneficiary: "", applicant: "", amount: "", expiry_date: "", goods_description: "", collateral_account_id: "", collateral_amount: "" });
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Trade Finance" title="Letters of Credit" subtitle="Documentary credits — sight, usance and standby — with beneficiary, applicant, and shipment terms." testid="lc-title"
        actions={<button onClick={() => setOpen(true)} data-testid="new-lc-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3]">+ New LC</button>} />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="lc-table">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Reference","Type","Applicant","Beneficiary","Amount","Ccy","Issue","Expiry","Collateral","Goods"].map((h) => <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{r.reference}</td>
                  <td className="px-4 py-3 text-[10px] uppercase font-bold tracking-wider">{r.lc_type}</td>
                  <td className="px-4 py-3">{r.applicant}</td>
                  <td className="px-4 py-3 font-semibold">{r.beneficiary}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.currency}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.issue_date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.expiry_date}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`lc-collateral-${r.id}`}>
                    {r.collateral_account_id ? (
                      <span><span className="font-mono">{(r.collateral_amount || 0).toLocaleString()}</span> <span className="text-gray-500">· {acctName(r.collateral_account_id)}</span></span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs max-w-[200px] truncate">{r.goods_description}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-500">No letters of credit issued yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="lc-form">
            <div className="p-6 border-b border-gray-200"><h2 className="text-2xl font-black tracking-tight">New Letter of Credit</h2></div>
            <div className="p-6 space-y-4">
              {[["reference","Reference / LC No.","text"],["applicant","Applicant","text"],["beneficiary","Beneficiary","text"],["amount","Amount","number"],["issue_date","Issue Date","date"],["expiry_date","Expiry Date","date"],["goods_description","Goods Description","text"]].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`lc-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">LC Type</label>
                  <select value={f.lc_type} onChange={(e) => setF({ ...f, lc_type: e.target.value })} data-testid="lc-type" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {["sight","usance","standby","revolving"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Currency</label>
                  <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} data-testid="lc-currency" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {["USD","EUR","GBP","AED","INR","JPY","CHF","SGD"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-3">Cash Collateral (optional)</div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Collateral Bank Account</label>
                <select value={f.collateral_account_id} onChange={(e) => setF({ ...f, collateral_account_id: e.target.value })} data-testid="lc-collateral-account" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">None</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.bank_name} · {a.currency}</option>)}
                </select>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5 mt-3">Collateral Amount ({f.currency})</label>
                <input type="number" step="any" value={f.collateral_amount} onChange={(e) => setF({ ...f, collateral_amount: e.target.value })} data-testid="lc-collateral-amount" placeholder="e.g. full or partial cover" className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200"><button type="submit" data-testid="lc-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Issue LC</button></div>
          </form>
        </div>
      )}
    </div>
  );
}
